// src/pages/RejectedLogs.jsx
// Control Panel → Logs (Super Admins), two tabs added in 3.10:
//   Rejected emails   emailed faxes that were refused (3.10.2 rule: fax@
//     for business-account members, privatefax@ for individual-account
//     members), with whether the sender got a notice. Block stops notices
//     to an address (spam); it's still refused and listed.
//   Blocked senders   (3.11.4) every blocked address: when, by whom, its
//     refused emails; Unblock
//   Blocked requests  calls to the backend's robot endpoints without the
//     right key or Telnyx signature. In log mode they were let through and
//     only listed here; in enforce mode they were refused.
// Entries are kept 90 days.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import ResetFiltersButton, { filtersDiffer } from '../components/ResetFiltersButton';
import EmptyState from '../components/EmptyState';
import { downloadSheet } from '../utils/api';
import PickFilters, { usePickFilters } from '../components/PickFilters';
import { API_BASE } from '../firebase/config';
import { timeZone, tzLabel } from '../utils/timeZone';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { useClientWord } from '../hooks/useClientWord';

const DEFAULTS = { startDate: '', endDate: '' };

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone(), month: 'short', day: 'numeric',
    year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) + ' ' + tzLabel(new Date(iso));
}

function formatFaxNum(num) {
  if (!num) return '';
  const n = String(num).replace(/\D/g, '');
  if (n.length === 11) return `+${n[0]} (${n.slice(1,4)}) ${n.slice(4,7)}-${n.slice(7)}`;
  if (n.length === 10) return `+1 (${n.slice(0,3)}) ${n.slice(3,6)}-${n.slice(6)}`;
  return num;
}

// Loads one of the two logs, with date filters (the type-ahead picks then
// filter the loaded rows; 3.18.9: Excel downloads exactly those rows)
function useLog(path) {
  const { getToken } = useAuth();
  const [filters, setFilters] = useState(DEFAULTS);
  const [applied, setApplied] = useState(DEFAULTS);
  const [data, setData]       = useState([]);
  const [extra, setExtra]     = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const params = f => {
    const p = new URLSearchParams();
    if (f.startDate) p.append('startDate', f.startDate);
    if (f.endDate) p.append('endDate', f.endDate);
    p.append('limit', '1000');
    return p;
  };

  const load = useCallback(async (f = filters) => {
    setLoading(true); setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/logs/${path}?${params(f)}`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json.data || []);
      setExtra(json.setup || null);
      setBlocked(json.blocked || []);
      setApplied(f);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [filters, getToken, path]);

  useEffect(() => { load(DEFAULTS); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { setFilters(DEFAULTS); load(DEFAULTS); };
  // Block / Unblock a sender (Rejected emails)
  async function block(email, on) {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/logs/${path}/block`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, blocked: on }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      setBlocked(list => (on ? [...new Set([...list, email])] : list.filter(e => e !== email)));
    } catch (e) { setError(e.message); }
  }

  return { filters, setFilters, applied, data, extra, blocked, block, loading, error, setError, load, reset };
}

function FilterBar({ log, picks, onDownload }) {
  const { filters, setFilters, load, reset } = log;
  const rows = picks.filtered;
  const set = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));
  return (
    <div style={S.filterBar}>
      <div style={S.filterRow}>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>From Date</label>
          <input type="date" value={filters.startDate} onChange={e => set('startDate', e.target.value)} style={S.filterInput} />
        </div>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>To Date</label>
          <input type="date" value={filters.endDate} onChange={e => set('endDate', e.target.value)} style={S.filterInput} />
        </div>
        {/* Type-ahead filters over what's loaded (3.18.8) */}
        <PickFilters state={picks} groupStyle={S.filterGroup} labelStyle={S.filterLabel} />
        <div style={{ ...S.filterGroup, justifyContent: 'flex-end', alignSelf: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => load()} style={S.searchBtn}><Emo e="🔍" /> Search</button>
            <ResetFiltersButton dirty={filtersDiffer(filters, DEFAULTS) || picks.active} onReset={() => { picks.reset(); reset(); }} />
            <button onClick={() => onDownload(rows)} disabled={!rows.length} style={{ ...S.downloadBtn, opacity: rows.length ? 1 : 0.5 }}
              title="Download the rows shown (these dates and filters) as an Excel file"><Emo e="⬇" /> Excel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Block is only for senders without a Fax Portal account (3.11.2): people
// in an account just get the notice telling them which address to use
const noAccount = r => r.kind === 'unregistered' || (!r.kind && /^No Fax Portal account|^Not in any account/.test(r.reason || ''));

export function RejectedEmails({ onOpenBlocked }) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken } = useAuth();
  const log = useLog('rejected');
  const picks = usePickFilters(log.data, [
    { key: 'sender', label: 'Sender', get: r => r.fromEmail, placeholder: 'Type a sender\'s email' },
    { key: 'reason', label: 'Reason', get: r => r.reason, placeholder: 'Type to find a reason' },
  ]);
  const rows = picks.filtered;
  const byGateway = rows.filter(r => r.stage === 'gateway').length;

  return (
    <div>
      <p style={S.intro}>
        Emails to fax@ or privatefax@crazypixels.org that weren't faxed because of who sent them. Only members can send:
        <strong> fax@</strong> for people in a business client, <strong>privatefax@</strong> for people in an individual client.
        Each sender gets a notice telling them why (at most one a day). An email sent to both addresses is never sent; the sender is asked to use one.
        <strong> Block</strong> (for addresses without a Fax Portal account) stops notices to spam: its emails are still refused and listed here.
        Kept 90 days.
      </p>
      {log.blocked.length > 0 && (
        <div style={S.setupStrip}>
          <span><Emo e="🚷" /> <strong>{log.blocked.length}</strong> blocked sender{log.blocked.length === 1 ? '' : 's'}: their emails are refused without a notice.</span>
          {onOpenBlocked && <button onClick={onOpenBlocked} style={S.rowBtn}>See Blocked Senders →</button>}
        </div>
      )}
      <FilterBar log={log} picks={picks} onDownload={list => downloadSheet('Refused Emails', [
        { header: 'When', get: r => formatDate(r.at), width: 24 }, { header: 'Sent to', get: r => `${r.gateway}@crazypixels.org`, width: 26 },
        { header: 'From', get: r => r.fromEmail, width: 32 }, { header: 'Subject', get: r => r.subject, width: 36 },
        { header: 'Fax number', get: r => r.toNumber, width: 16 }, { header: 'File', get: r => r.fileName, width: 28 },
        { header: 'Refused by', get: r => (r.stage === 'gateway' ? 'Email gateway' : 'Backend'), width: 16 }, { header: 'Reason', get: r => r.reason, width: 50 },
        { header: clientWord, get: r => r.accountName, width: 20 },
        { header: 'Notice', get: r => (r.blocked ? 'No (blocked)' : r.noticeSent ? 'Sent' : 'No (already told today)'), width: 22 },
        { header: 'Blocked now', get: r => (log.blocked.includes(r.fromEmail) ? 'Yes' : ''), width: 12 },
      ], list, getToken).catch(e => log.setError(e.message))} />
      <div style={S.resultCount}>
        {log.loading ? 'Loading…' : `${rows.length} rejected email${rows.length !== 1 ? 's' : ''}${rows.length ? ` · ${byGateway} by the email gateway, ${rows.length - byGateway} by the backend` : ''}`}
      </div>
      {log.error && <div style={S.errorBanner}>{log.error}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <th style={S.th}>When</th>
              <th style={S.th}>Sent to</th>
              <th style={S.th}>From</th>
              <th style={S.th}>Subject / Fax #</th>
              <th style={S.th}>Refused by</th>
              <th style={S.th}>Reason</th>
              <th style={S.th}>Notice</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {log.loading ? (
              <tr><td colSpan={8} style={S.emptyCell}><Loader /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={S.emptyCell}>
                <EmptyState icon="🚫" filtered={filtersDiffer(log.applied, DEFAULTS) || picks.active} onClear={() => { picks.reset(); log.reset(); }}
                  filteredText="No rejected emails match these filters."
                  emptyText="No rejected emails. Every emailed fax came from someone allowed to send it." />
              </td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' }}>{formatDate(r.at)}
                  {r.accountName && <div style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{r.accountName}</div>}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{r.gateway}@</td>
                <td style={{ ...S.td, fontWeight: 500 }}>{r.fromEmail || '—'}</td>
                <td style={S.td}>{r.subject || formatFaxNum(r.toNumber) || '—'}
                  {r.fileName && <div style={{ fontSize: 11, color: 'var(--t-78909c)' }}>{r.fileName}</div>}</td>
                <td style={S.td}>
                  <span style={{ ...S.badge, ...(r.stage === 'gateway' ? S.badgeGateway : S.badgeBackend) }}>
                    {r.stage === 'gateway' ? 'Email gateway' : 'Backend'}
                  </span>
                </td>
                <td style={S.td}>{r.reason || '—'}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>
                  {r.blocked ? <span style={{ ...S.badge, ...S.badgeBackend }}>Blocked</span>
                    : r.noticeSent ? <><Emo e="✉️" /> Sent</>
                    : r.kind === 'role' ? 'Gateway told them'
                    : 'Already told today'}
                </td>
                <td style={S.td}>
                  {r.fromEmail && (log.blocked.includes(r.fromEmail)
                    ? <button onClick={() => log.block(r.fromEmail, false)} style={S.rowBtn} title="They get notices again">Unblock</button>
                    : noAccount(r) && <button onClick={() => log.block(r.fromEmail, true)} style={{ ...S.rowBtn, ...S.rowBtnDanger }} title="Spam: keep refusing, never send a notice">Block</button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Blocked senders (3.11.4) ───────────────────────────────
export function BlockedSenders() {
  const { getToken } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/logs/blocked-senders`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json.data || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [getToken]);
  // Loaded once, like the other logs (Refresh reloads)
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function unblock(email) {
    setBusy(email); setError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/logs/rejected/block`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, blocked: false }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      setData(list => list.filter(r => r.email !== email));
    } catch (e) { setError(e.message); }
    setBusy('');
  }

  // Excel of the rows shown (3.18.9)
  async function download() {
    try {
      await downloadSheet('Blocked Senders', [
        { header: 'Address', get: r => r.email, width: 34 }, { header: 'Blocked', get: r => formatDate(r.blockedAt), width: 24 },
        { header: 'Blocked by', get: r => r.blockedBy, width: 28 }, { header: 'Refused emails (90 days)', get: r => r.refused, width: 22 },
        { header: 'Last attempt', get: r => formatDate(r.lastAt), width: 24 },
        { header: 'Last sent to', get: r => (r.lastGateway ? `${r.lastGateway}@crazypixels.org` : ''), width: 26 },
      ], picks.filtered, getToken);
    } catch (e) { setError(e.message); }
  }

  const picks = usePickFilters(data, [
    { key: 'email', label: 'Address', get: r => r.email, placeholder: 'Type a blocked address' },
    { key: 'by', label: 'Blocked by', get: r => r.blockedBy, placeholder: 'Type who blocked it' },
  ]);
  const rows = picks.filtered;
  const term = picks.active;

  return (
    <div>
      <p style={S.intro}>
        Addresses blocked from Refused Emails (spam, or anyone who shouldn't get our notices). Their emails to fax@ or privatefax@ are still refused and listed in Refused Emails,
        but they never get a notice. <strong>Unblock</strong> if one was blocked by mistake: they get notices again (at most one a day). Only addresses without a Fax Portal account can be blocked.
      </p>
      <div style={S.filterBar}>
        <div style={S.filterRow}>
          <PickFilters state={picks} groupStyle={S.filterGroup} labelStyle={S.filterLabel} />
          <div style={{ ...S.filterGroup, justifyContent: 'flex-end', alignSelf: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={load} style={S.searchBtn}><Emo e="↻" /> Refresh</button>
              <button onClick={download} disabled={!rows.length} style={{ ...S.downloadBtn, opacity: rows.length ? 1 : 0.5 }} title="Download the rows shown as an Excel file"><Emo e="⬇" /> Excel</button>
            </div>
          </div>
        </div>
      </div>
      <div style={S.resultCount}>{loading ? 'Loading…' : `${rows.length} blocked sender${rows.length !== 1 ? 's' : ''}${term && rows.length !== data.length ? ` (of ${data.length})` : ''}`}</div>
      {error && <div style={S.errorBanner}>{error}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <th style={S.th}>Address</th>
              <th style={S.th}>Blocked</th>
              <th style={S.th}>Blocked by</th>
              <th style={S.th}>Refused emails (90 days)</th>
              <th style={S.th}>Last attempt</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={S.emptyCell}><Loader /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={S.emptyCell}>
                <EmptyState icon="🚷" filtered={!!term} onClear={picks.reset}
                  filteredText="No blocked senders match your search."
                  emptyText="Nobody is blocked. Block spam senders from Refused Emails." />
              </td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.email} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                <td style={{ ...S.td, fontWeight: 500 }}>{r.email}</td>
                <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' }}>{formatDate(r.blockedAt)}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{r.blockedBy || '—'}</td>
                <td style={S.td}>{r.refused}</td>
                <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' }}>{r.lastAt ? <>{formatDate(r.lastAt)}<div style={{ fontSize: 11 }}>to {r.lastGateway}@</div></> : '—'}</td>
                <td style={S.td}>
                  <button onClick={() => unblock(r.email)} disabled={busy === r.email} style={S.rowBtn} title="They get notices again">
                    {busy === r.email ? 'Unblocking…' : 'Unblock'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const CHECKS = {
  gateway_key     : 'Gateway key',
  telnyx_signature: 'Telnyx signature',
};

export function BlockedRequests() {
  const { getToken } = useAuth();
  const log = useLog('blocked');
  const picks = usePickFilters(log.data, [
    { key: 'path', label: 'Endpoint', get: r => r.path, placeholder: 'Type to find an endpoint' },
    { key: 'reason', label: 'Reason', get: r => r.reason, placeholder: 'Type to find a reason' },
    { key: 'ip', label: 'IP address', get: r => r.ip, placeholder: 'Type an IP address' },
  ]);
  const rows = picks.filtered;
  const setup = log.extra;
  const times = rows.reduce((n, r) => n + (r.count || 0), 0);

  return (
    <div>
      <p style={S.intro}>
        Calls to the backend's machine endpoints (email gateways, reports, Telnyx) that didn't carry the right key or Telnyx signature.
        While the checks are in <strong>log only</strong> mode these calls still went through, so faxes keep working; once this list stays empty
        for real traffic, the checks can be switched to <strong>enforce</strong>, which refuses them. One row per day, endpoint, reason and address. Kept 90 days.
      </p>
      {setup && (
        <div style={S.setupStrip}>
          <span style={S.setupTitle}>Checks</span>
          <span style={S.setupItem}>Mode: <strong style={{ color: setup.mode === 'enforce' ? 'var(--t-2e7d32)' : 'var(--t-e65100)' }}>{setup.mode === 'enforce' ? 'Enforce (refused)' : 'Log only (let through)'}</strong></span>
          <span style={S.setupItem}><Emo e={setup.gatewayKey ? '✅' : '⚠️'} /> Gateway key {setup.gatewayKey ? 'set up' : 'not set up'}</span>
          <span style={S.setupItem}><Emo e={setup.telnyxKey ? '✅' : '⚠️'} /> Telnyx public key {setup.telnyxKey ? 'set up' : 'not set up'}</span>
          <span style={S.setupItem}><Emo e={setup.schedulerKey ? '✅' : '⚠️'} /> Scheduled jobs key {setup.schedulerKey ? 'set up' : 'not set up'}</span>
        </div>
      )}
      <FilterBar log={log} picks={picks} onDownload={list => downloadSheet('Blocked Connections', [
        { header: 'Last seen', get: r => formatDate(r.lastAt), width: 24 }, { header: 'Day (UTC)', get: r => r.day, width: 12 },
        { header: 'Check', get: r => (r.kind === 'telnyx_signature' ? 'Telnyx signature' : 'Gateway key'), width: 18 },
        { header: 'Endpoint', get: r => `${r.method || ''} ${r.path}`, width: 34 }, { header: 'Reason', get: r => r.reason, width: 44 },
        { header: 'Times', get: r => r.count, width: 8 }, { header: 'Mode', get: r => (r.mode === 'enforce' ? 'Refused' : 'Let through (log only)'), width: 22 },
        { header: 'IP address', get: r => r.ip, width: 18 }, { header: 'User agent', get: r => r.userAgent, width: 40 },
      ], list, getToken).catch(e => log.setError(e.message))} />
      <div style={S.resultCount}>
        {log.loading ? 'Loading…' : `${rows.length} row${rows.length !== 1 ? 's' : ''}${rows.length ? ` · ${times} call${times !== 1 ? 's' : ''}` : ''}`}
      </div>
      {log.error && <div style={S.errorBanner}>{log.error}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <th style={S.th}>Last seen</th>
              <th style={S.th}>Check</th>
              <th style={S.th}>Endpoint</th>
              <th style={S.th}>Reason</th>
              <th style={S.th}>Times</th>
              <th style={S.th}>Result</th>
              <th style={S.th}>From</th>
            </tr>
          </thead>
          <tbody>
            {log.loading ? (
              <tr><td colSpan={7} style={S.emptyCell}><Loader /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} style={S.emptyCell}>
                <EmptyState icon="🛡️" filtered={filtersDiffer(log.applied, DEFAULTS) || picks.active} onClear={() => { picks.reset(); log.reset(); }}
                  filteredText="No blocked requests match these filters."
                  emptyText="No blocked requests. Every call carried the right key or signature." />
              </td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.id} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                <td style={{ ...S.td, fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' }}>{formatDate(r.lastAt)}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{CHECKS[r.kind] || r.kind}</td>
                <td style={S.td}><code style={S.code}>{r.method} {r.path}</code></td>
                <td style={S.td}>{r.reason}</td>
                <td style={S.td}>{r.count || 1}</td>
                <td style={S.td}>
                  <span style={{ ...S.badge, ...(r.mode === 'enforce' ? S.badgeBackend : S.badgeGateway) }}>
                    {r.mode === 'enforce' ? 'Refused' : 'Let through'}
                  </span>
                </td>
                <td style={{ ...S.td, fontSize: 12 }}>{r.ip || '—'}
                  {r.userAgent && <div style={{ fontSize: 11, color: 'var(--t-78909c)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.userAgent}>{r.userAgent}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  intro       : { color: 'var(--t-546e7a)', fontSize: 13, lineHeight: 1.6, margin: '0 0 16px', maxWidth: 980 },
  setupStrip  : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18, background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '12px 20px', marginBottom: 16, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', fontSize: 13, color: 'var(--t-546e7a)' },
  setupTitle  : { fontWeight: 700, color: 'var(--t-00695c)', fontSize: 14 },
  setupItem   : { whiteSpace: 'nowrap' },
  filterBar   : { background: 'var(--c-ffffff)', borderRadius: 12, padding: '20px 24px', marginBottom: 16, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', border: '1px solid var(--b-e8f5e9)' },
  filterRow   : { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' },
  filterGroup : { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 },
  filterLabel : { fontSize: 12, fontWeight: 600, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  filterInput : { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, color: 'var(--t-333333)', fontFamily: 'inherit' },
  searchBtn   : { padding: '9px 20px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  downloadBtn : { padding: '9px 16px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-00897b)', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  resultCount : { fontSize: 14, color: 'var(--t-546e7a)', marginBottom: 12 },
  errorBanner : { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  tableWrap   : { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table       : { width: '100%', borderCollapse: 'collapse' },
  thead       : { background: 'var(--c-00695c)' },
  th          : { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr          : { borderBottom: '1px solid var(--c-f0f0f0)' },
  td          : { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle' },
  badge       : { padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  badgeGateway: { background: 'var(--c-fff3e0)', color: 'var(--t-e65100)' },
  badgeBackend: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)' },
  rowBtn      : { padding: '5px 12px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  rowBtnDanger: { borderColor: 'var(--c-ef9a9a)', color: 'var(--t-b71c1c)' },
  code        : { fontSize: 12, color: 'var(--t-37474f)', background: 'var(--c-eceff1)', padding: '2px 6px', borderRadius: 4 },
  emptyCell   : { textAlign: 'center', padding: '40px 0' },
};
